import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logger } from '@/lib/logger';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Barreira de erro da aplicação (F20-T01).
 *
 * Mostra uma mensagem legível e guarda o detalhe técnico atrás de um `<details>`.
 * Stack trace nunca aparece de cara para o usuário (F20-T05), mas precisa estar
 * acessível para diagnóstico — o app é usado offline, sem canal de suporte.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error('app', 'erro não tratado na árvore de componentes', error);
    console.error(info.componentStack);
  }

  private readonly reload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-safe py-10 text-center">
        <h1 className="text-xl font-semibold">Algo deu errado</h1>
        <p className="max-w-sm text-sm text-slate-400">
          A aplicação encontrou um erro inesperado. Seus dados offline — favoritos,
          progresso e vídeos baixados — não foram afetados.
        </p>

        <button
          type="button"
          onClick={this.reload}
          className="min-h-11 rounded-lg bg-sky-500 px-5 font-medium text-slate-950 active:bg-sky-600"
        >
          Recarregar
        </button>

        <details className="mt-4 max-w-full text-left">
          <summary className="cursor-pointer text-xs text-slate-500">Detalhes técnicos</summary>
          <pre className="mt-2 max-w-[90vw] overflow-x-auto rounded bg-slate-800 p-3 text-xs text-slate-300">
            {error.message}
          </pre>
        </details>
      </div>
    );
  }
}
